import { EAttributeType } from '../@types/AttributeTypes';
import AttributeModel from '../models/standard/AttributeModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';
import UserModel from '../models/standard/UserModel';

/**
 * Method to parse the response json object from the login endpoint and combine it with the data from the login input.
 * @param {Object} loginJsonObject the json object response from the endpoint.
 * @param {string} email string email value gathered from the login input.
 * @param {string} [password] string password value gathered from the login input (unused in model, but kept for signature compatibility).
 * @returns {UserModel} object containing all available data points as a user model.
 */
export const parseUserLoginJsonObject = (loginJsonObject, email, password) => {
  // Initialize with defaults
  let userData = new UserModel('', '', email, '', '', '', '', '', '', '', '', '');

  try {
    const result = loginJsonObject.result;
    const user = result.user || {};

    // Helper to extract ID string whether it's a direct string or a mongo $numberLong object
    const extractId = (val) => {
      if (!val) return "";
      if (typeof val === 'object' && val.$numberLong) return val.$numberLong;
      return val.toString();
    };

    userData = new UserModel(
      result.authentificationtoken || "",                     // 1. Token
      user.localization || "de_DE",                           // 2. Localization
      user.email || email,                                    // 3. Email
      user.first_name || "",                                  // 4. FirstName
      user.last_name || "",                                   // 5. LastName
      user.company || "",                                     // 6. Company
      extractId(user.id),                                     // 7. ID
      extractId(user.profile_image_id),                       // 8. ProfileImageID
      "",                                                     // 9. ProfileImageDataId (Not in JSON example)
      result.version_major ? result.version_major.toString() : "0", // 10. Major
      result.version_minor ? result.version_minor.toString() : "0", // 11. Minor
      user.country || ""                                      // 12. Country (e.g. "DE")
    );

  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    console.error('Error occurred while parsing the user login json response! ErrorMessage:\n' + message);
  }
  return userData;
};


 /**
  * Method to parse the json object that was gathered via the user endpoint and contains all the user data.
  * @param {Object} userDataJsonObject the json object containing all data.
  * @param {string} token string authentificationtoken that was already used in requesting this data.
  * @returns {UserModel} object user model containing all data values.
  */
 export const parseUserDataJsonObject = (userDataJsonObject, token) => {
   // Initialize with defaults
   let userData = new UserModel(token, '', '', '', '', '', '', '', '', '', '', '');
 
   try {
     const user = userDataJsonObject.result.user || {};
 
     // Helper to extract ID string whether it's a direct string or a mongo $numberLong object
     const extractId = (val) => {
       if (!val) return "";
       if (typeof val === 'object' && val.$numberLong) return val.$numberLong;
       return val.toString();
     };
 
     // Determine profile image data ID safely
     let profileImageDataId = "";
     if (user.profile_image && user.profile_image.thumb_res_image_data_mid) {
       profileImageDataId = user.profile_image.thumb_res_image_data_mid;
     }
 
     // Determine profile image ID safely
     const profileImageId = extractId(user.profile_image_id);
     // If ID is '0', we treat it as empty/no image (matching your previous logic)
     const finalProfileImageId = profileImageId === '0' ? '' : profileImageId;
     const finalProfileImageDataId = profileImageId === '0' ? '' : profileImageDataId;
 
     userData = new UserModel(
       token,                                                  // 1. Token
       user.localization || "",                                // 2. Localization
       user.email || "",                                       // 3. Email
       user.first_name || "",                                  // 4. FirstName
       user.last_name || "",                                   // 5. LastName
       user.company || "",                                     // 6. Company
       extractId(user.id),                                     // 7. ID
       finalProfileImageId,                                    // 8. ProfileImageID
       finalProfileImageDataId,                                // 9. ProfileImageDataId
       userData.versionMajor || "0",                           // 10. Major (preserve existing or default)
       userData.versionMinor || "0",                           // 11. Minor (preserve existing or default)
       user.country || ""                                      // 12. Country 
     );
 
   } catch (e) {
     let message = 'An unknown error occurred!';
     if (e instanceof Error) {
       message = e.message;
     }
     console.error('Error occurred while parsing a user data object! ErrorMessage:\n' + message);
   }
   return userData;
 };
/**
 * Function to retrieve the authentification token from the request response after a successful login to the imsFilesApi.
 *
 * @param {IFilesApiUser} userLoginJsonResponse the JSON response object that gets returned after a successful login attempt to the imsFilesApi.
 */
export const parseFilesApiUserLoginJSON = (userLoginJsonResponse) => userLoginJsonResponse.accessToken;

/**
 * Function to iterate over the json response from the imsFilesApi that contains all products.
 * Collects all identifiers of the products and returns them in a number array.
 * Is needed so that we can access the attributes of the individual products.
 *
 * @param {IProduct[]} productRequestJsonResponse the json object that was fetched via the products/all endpoint containing all indizes.
 * @returns the extracted indizes as a number array.
 */
export const extractProductIdsFromResponse = (productRequestJsonResponse) => {
  const productIds = [];
  try {
    for (let i = 0; i < productRequestJsonResponse.length; i++) {
      const tmpProduct = parseFilesApiSingleProductRequest(productRequestJsonResponse[i]);
      const tmpId = tmpProduct.id;
      productIds.push(tmpId);
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while extracting the Ids from the product object array! ErrorMessage:\n' + message);
  }
  return productIds;
};

/**
 * Function to retrieve an array of products that get fetched from the imsFilesApi.
 *
 * @param {IProduct[]} productRequestJsonResponse the JSON response object that gets returned after all products get retrieved from the imsFilesApi.
 */
export const parseFilesApiProductRequest = (productRequestJsonResponse) => {
  const products = [];
  try {
    for (let i = 0; i < productRequestJsonResponse.length; i++) {
      products.push(parseFilesApiSingleProductRequest(productRequestJsonResponse[i]));
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing the product object array! ErrorMessage:\n' + message);
  }
  return products;
};

/**
 * Function to parse a single product JSON object that was retrieved via the imsFilesApi.
 *
 * @param singleProductJson single product as a json object.
 * @returns {FilesAPIProductModel} model representing a single product.
 */
 
 export const parseFilesApiSingleProductRequest = (singleProductJson) => {
   try {
     // Helper to map API attributes
     const mapAttributes = (attrs) => {
       return (attrs || []).map((wrapper, i) => {
         const def = wrapper.attribute || wrapper;
         const value = wrapper.value !== undefined && wrapper.value !== null ? wrapper.value : def.value;
         const unit = wrapper.unit !== undefined && wrapper.unit !== null ? wrapper.unit : def.unit;
         const predecessorId = i === 0 ? null : (attrs[i - 1].attribute?.id || attrs[i - 1].id);
 
         const typeMap = {
           TEXT: EAttributeType.TEXT,
           NUMBER: EAttributeType.NUMBER,
           BOOL: EAttributeType.BOOL,
           SELECTION: EAttributeType.SELECTION,
           PLAIN: EAttributeType.PLAIN
         };
         const type = typeMap[def.type] || EAttributeType.TEXT;
 
         return new AttributeModel(
           Number(def.id),
           def.key || '',
           value, 
           predecessorId,
           type,
           unit,
           wrapper.hidden ?? def.hidden ?? false,
           wrapper.forced ?? def.forced ?? false,
           def.ownerId,
           def.userId,
           def.publicFlag,
           def.localization,
           def.defaults,
           def.validation,
           def.unitFamily
         );
       });
     };
 
     const productAttributes = mapAttributes(singleProductJson.attributes);
 
     const parsedObjects = (singleProductJson.objects || []).map((obj) => ({
       ...obj,
       attributes: [] // Set empty to prevent ghosting (we rely on productAttributes)
     }));
 
     return new FilesAPIProductModel(
       singleProductJson.id,
       singleProductJson.ownerId,
       singleProductJson.publicFlag,
       singleProductJson.productId,
       singleProductJson.name,
       singleProductJson.description,
       singleProductJson.manufacturer,
       singleProductJson.isFavorite,
       singleProductJson.links || [],
       productAttributes, 
       singleProductJson.assets || [],
       parsedObjects,
       singleProductJson.data || [],
       '', 
       singleProductJson.previewPicture,
       [], // materialAssets
       [], // modelAssets
       
       singleProductJson.depth || 0,    
       singleProductJson.length || 0,   
       singleProductJson.country        
     );
   } catch (e) {
     let message = 'An unknown error occurred!';
     if (e instanceof Error) {
       message = e.message;
     }
     console.error('Error occurred while parsing a single product object! ErrorMessage:\n' + message);
     throw e;
   }
 };
/**
 * Function to parse the attributes JSON array that was retrieved via the imsFilesApi.
 *
 * @param attributesApiResponse all attributes as a JSON array.
 * @returns {string[]} the attributes names as strings in an array.
 */
export const parseFilesApiAttributesRequestAsStrings = (attributesApiResponse) => {
  let attributes = [];
  try {
    for (let i = 0; i < attributesApiResponse.length; i++) {
      attributes.push(attributesApiResponse[i].key);
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing attribute names! ErrorMessage:\n' + message);
  }
  return attributes;
};

/**
 * Function to parse the categories JSON array that was retrieved via the imsFilesApi.
 *
 * @param categoriesApiResponse all categories as a JSON array.
 * @returns {string[]} the categories names as strings in an array.
 */
export const parseFilesApiCategoriesRequestAsStrings = (categoriesApiResponse) => {
  let categories = [];
  try {
    for (let i = 0; i < categoriesApiResponse.length; i++) {
      categories.push(categoriesApiResponse[i].name);
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing attribute names! ErrorMessage:\n' + message);
  }
  return categories;
};

/**
 * Function to parse the manufacturers JSON array that was retrieved via the imsFilesApi.
 *
 * @param manufacturersApiResponse all manufacturers as a JSON array.
 * @returns {IManufacturer[]} the manufacturers in an array.
 */
export const parseFilesApiManufacturersRequest = (manufacturersApiResponse) => {
  let manufacturers = [];
  try {
    for (let i = 0; i < manufacturersApiResponse.length; i++) {
      manufacturers.push({
        id: manufacturersApiResponse[i].id,
        name: manufacturersApiResponse[i].name,
      });
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing manufacturer objects! ErrorMessage:\n' + message);
  }
  return manufacturers;
};

/**
 * Function to parse the categories JSON array that was retrieved via the imsFilesApi.
 *
 * @param categoriesApiResponse all categories as a JSON array.
 * @returns {IObject[]} the categories in an array.
 */
export const parseFilesApiCategoriesRequest = (categoriesApiResponse) => {
  let categories = [];
  try {
    for (let i = 0; i < categoriesApiResponse.length; i++) {
      categories.push({
        id: categoriesApiResponse[i].id,
        name: categoriesApiResponse[i].name,
      });
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing product category objects! ErrorMessage:\n' + message);
  }
  return categories;
};

/**
 * Function to parse the material JSON object that was retrieved via the imsFilesApi as an asset of a product.
 *
 * @param materialApiResponse the material as a JSON object.
 * @returns {IMaterial} the material in our internal component representation.
 */
export const parseFilesApiMaterialRequest = (materialApiResponse) => {
  try {
    // Extracted preview picture and maps to avoid null accessing.
    let previewPicture = null;
    let object = null;

    if (materialApiResponse.previewPicture !== null && materialApiResponse.previewPicture !== undefined) {
      previewPicture = {
        id: materialApiResponse.previewPicture.id,
        ownerId: materialApiResponse.previewPicture.ownerId,
        filePath: materialApiResponse.previewPicture.filePath,
        fileName: materialApiResponse.previewPicture.fileName,
        fileFormat: materialApiResponse.previewPicture.fileFormat,
        fileSize: materialApiResponse.previewPicture.fileSize,
        creationDate: materialApiResponse.previewPicture.creationDate,
        modificationDate: materialApiResponse.previewPicture.modificationDate,
        lowResPath: materialApiResponse.previewPicture.lowResPath,
        mediumResPath: materialApiResponse.previewPicture.mediumResPath,
        highResPath: materialApiResponse.previewPicture.highResPath,
        thumbnailPath: materialApiResponse.previewPicture.thumbnailPath,
      };
    }

    // Parsing 'object'
    if (materialApiResponse.object !== null && materialApiResponse.object !== undefined) {
      object = {
        id: materialApiResponse.object.id,
        name: materialApiResponse.object.name,
        ownerId: materialApiResponse.object.ownerId,
        publicFlag: materialApiResponse.object.publicFlag,
      };
    }

    const maps = [];
    if (materialApiResponse.maps.length > 0) {
      for (let i = 0; i < materialApiResponse.maps.length; i++) {
        let image = null;
        if (materialApiResponse.maps[i].image !== null && materialApiResponse.maps[i].image !== undefined) {
          image = {
            id: materialApiResponse.maps[i].image.id,
            ownerId: materialApiResponse.maps[i].image.ownerId,
            filePath: materialApiResponse.maps[i].image.filePath,
            fileName: materialApiResponse.maps[i].image.fileName,
            fileFormat: materialApiResponse.maps[i].image.fileFormat,
            fileSize: materialApiResponse.maps[i].image.fileSize,
            creationDate: materialApiResponse.maps[i].image.creationDate,
            modificationDate: materialApiResponse.maps[i].image.modificationDate,
            lowResPath: materialApiResponse.maps[i].image.lowResPath,
            mediumResPath: materialApiResponse.maps[i].image.mediumResPath,
            highResPath: materialApiResponse.maps[i].image.highResPath,
            thumbnailPath: materialApiResponse.maps[i].image.thumbnailPath,
          };
        }
        const tmpMap = {
          id: materialApiResponse.maps[i].id,
          image: image,
          type: materialApiResponse.maps[i].type,
        };
        maps.push(tmpMap);
      }
    }
    const material = {
      id: materialApiResponse.id,
      ownerId: materialApiResponse.ownerId,
      publicFlag: materialApiResponse.publicFlag,
      name: materialApiResponse.name,
      assetType: materialApiResponse.assetType,
      object: object,
      previewPicture: previewPicture,
      color: materialApiResponse.color,
      emissiveColor: materialApiResponse.emissiveColor,
      emissiveIntensity: materialApiResponse.emissiveIntensity,
      roughness: materialApiResponse.roughness,
      metalness: materialApiResponse.metalness,
      reflectivity: materialApiResponse.reflectivity,
      clearcoat: materialApiResponse.clearcoat,
      clearcoatRoughness: materialApiResponse.clearcoatRoughness,
      sheen: materialApiResponse.sheen,
      sheenRoughness: materialApiResponse.sheenRoughness,
      transmission: materialApiResponse.transmission,
      thickness: materialApiResponse.thickness,
      opacity: materialApiResponse.opacity,
      side: materialApiResponse.side,
      flatShading: materialApiResponse.flatShading,
      blending: materialApiResponse.blending,
      transparent: materialApiResponse.transparent,
      alphaTest: materialApiResponse.alphaTest,
      depthTest: materialApiResponse.depthTest,
      depthWrite: materialApiResponse.depthWrite,
      width: materialApiResponse.width,
      height: materialApiResponse.height,
      bumpScale: materialApiResponse.bumpScale,
      normalScaleX: materialApiResponse.normalScaleX,
      normalScaleY: materialApiResponse.normalScaleY,
      aoMapIntensity: materialApiResponse.aoMapIntensity,
      displacementScale: materialApiResponse.displacementScale,
      displacementBias: materialApiResponse.displacementBias,
      maps: maps,
    };
    return material;
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing a product material asset! ErrorMessage:\n' + message);
  }
};

/**
 * Function to parse the model JSON object that was retrieved via the imsFilesApi as an asset of a product.
 *
 * @param modelApiResponse the model as a JSON object.
 * @returns {I3dModel} the model in our internal component representation.
 */
export const parseFilesApiModelRequest = (modelApiResponse) => {
  try {
    // Extracted objects to avoid null accessing.
    let previewPicture = null;
    let file2d = null;
    let object = null;

    if (modelApiResponse.file2d !== null && modelApiResponse.file2d !== undefined) {
      file2d = {
        id: modelApiResponse.file2d.id,
        ownerId: modelApiResponse.file2d.ownerId,
        filePath: modelApiResponse.file2d.filePath,
        fileName: modelApiResponse.file2d.fileName,
        fileFormat: modelApiResponse.file2d.fileFormat,
        fileSize: modelApiResponse.file2d.fileSize,
        creationDate: modelApiResponse.file2d.creationDate,
        modificationDate: modelApiResponse.file2d.modificationDate,
      };
    }

    if (modelApiResponse.previewPicture !== null && modelApiResponse.previewPicture !== undefined) {
      previewPicture = {
        id: modelApiResponse.previewPicture.id,
        ownerId: modelApiResponse.previewPicture.ownerId,
        filePath: modelApiResponse.previewPicture.filePath,
        fileName: modelApiResponse.previewPicture.fileName,
        fileFormat: modelApiResponse.previewPicture.fileFormat,
        fileSize: modelApiResponse.previewPicture.fileSize,
        creationDate: modelApiResponse.previewPicture.creationDate,
        modificationDate: modelApiResponse.previewPicture.modificationDate,
        lowResPath: modelApiResponse.previewPicture.lowResPath,
        mediumResPath: modelApiResponse.previewPicture.mediumResPath,
        highResPath: modelApiResponse.previewPicture.highResPath,
        thumbnailPath: modelApiResponse.previewPicture.thumbnailPath,
      };
    }

    // Parsing 'object'
    if (modelApiResponse.object !== null && modelApiResponse.object !== undefined) {
      object = {
        id: modelApiResponse.object.id,
        name: modelApiResponse.object.name,
        ownerId: modelApiResponse.object.ownerId,
        publicFlag: modelApiResponse.object.publicFlag,
      };
    }

    const model = {
      id: modelApiResponse.id,
      ownerId: modelApiResponse.ownerId,
      publicFlag: modelApiResponse.publicFlag,
      name: modelApiResponse.name,
      assetType: modelApiResponse.assetType,
      object: object,
      previewPicture: previewPicture,
      filePath: modelApiResponse.filePath,
      icon: modelApiResponse.icon,
      filename: modelApiResponse.filename,
      fileFormat: modelApiResponse.fileFormat,
      fileSize: modelApiResponse.fileSize,
      length: modelApiResponse.length,
      height: modelApiResponse.height,
      depth: modelApiResponse.depth,
      scale: modelApiResponse.scale,
      creationDate: modelApiResponse.creationDate,
      modificationDate: modelApiResponse.modificationDate,
      file2d: file2d,
      rotation2D: modelApiResponse.rotation2D,
    };
    return model;
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    // console.log('Error occurred while parsing a product model asset! ErrorMessage:\n' + message);
  }
};

/**
 * Function to parse the attributes JSON array that was retrieved via the imsFilesApi.
 *
 * @param attributesApiResponse all attributes as a JSON array.
 * @returns {IAttribute[]} the attributes in an array.
 */
/**
 * Function to parse the attributes JSON array that was retrieved via the imsFilesApi.
 *
 * @param {Array} attributesApiResponse all attributes as a JSON array.
 * @returns {Array} the attributes in an array matching the AttributeModel structure.
 */
 export const parseFilesApiAttributesRequest = (attributesApiResponse) => {
  let attributes = [];
  try {
    for (let i = 0; i < attributesApiResponse.length; i++) {
      const item = attributesApiResponse[i];

      attributes.push({
        // --- 1. Standard/Legacy Fields ---
        id: item.id,
        key: item.key,
        type: item.type, // e.g. 'TEXT', 'NUMBER'
        
        // Use || null to handle cases where the new API might omit 'unit'
        unit: item.unit || null,
        
        // Provide defaults for fields required by your Model but missing in the JSON
        predecessorId: item.predecessorId || null,
        hidden: item.hidden || false,
        forced: item.forced || false,
        
        // If 'value' exists (legacy), use it; otherwise undefined (let defaults logic handle it later)
        value: item.value, 

        unitFamily: item.unitFamily,
        ownerId: item.ownerId,       
        userId: item.userId,       
        publicFlag: item.publicFlag,
        localization: item.localization,
        defaults: item.defaults,       
        validation: item.validation    
      });
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    // Assuming reportError is available in your scope
    reportError('Error occurred while parsing attribute objects! ErrorMessage:\n' + message);
  }
  return attributes;
};

/**
 * Function to parse the materials JSON array that was retrieved for the texture drawer via the imsFilesApi.
 *
 * @param materialsApiResponse all materials as a JSON array.
 * @returns {IFilesApiMaterial[]} the materials in an array.
 */
export const parseFilesApiBatchMaterialsRequest = (materialsApiResponse) => {
  let materials = [];
  try {
    for (let i = 0; i < materialsApiResponse.length; i++) {
      materials.push({
        id: materialsApiResponse[i].id,
        ownerId: materialsApiResponse[i].ownerId,
        name: materialsApiResponse[i].name,
        assetType: materialsApiResponse[i].assetType,
        object: materialsApiResponse[i].object,
        width: materialsApiResponse[i].width,
        height: materialsApiResponse[i].height,
        previewPictureId: materialsApiResponse[i].previewPictureId,
      });
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing material texture objects! ErrorMessage:\n' + message);
  }
  return materials;
};

/**
 * Function to parse the colors JSON array that was retrieved for the texture drawer via the imsFilesApi.
 *
 * @param colorsApiResponse all colors as a JSON array. (content field of the original api request).
 * @returns {IFilesApiColor[]} the colors in an array.
 */
export const parseFilesApiBatchColorsRequest = (colorsApiResponse) => {
  let colors = [];
  try {
    for (let i = 0; i < colorsApiResponse.length; i++) {
      colors.push({
        id: colorsApiResponse[i].id,
        ownerId: colorsApiResponse[i].ownerId,
        name: colorsApiResponse[i].name,
        assetType: colorsApiResponse[i].assetType,
        object: colorsApiResponse[i].object,
        hex: colorsApiResponse[i].hex,
        ral: colorsApiResponse[i].ral,
      });
    }
  } catch (e) {
    let message = 'An unknown error occurred!';
    if (e instanceof Error) {
      message = e.message;
    }
    reportError('Error occurred while parsing color texture objects! ErrorMessage:\n' + message);
  }
  return colors;
};
